import {
  createUnifiedTheme,
  palettes,
  genPageTheme,
  shapes,
} from '@backstage/theme';
import BaseSky1 from '../assets/Base_sky_1.png';
import BaseSky6 from '../assets/Base_sky_6.png';

const arrivePageTheme = genPageTheme({
  colors: ['#5F016F', '#FF80D4'], /* P1 to P2 */
  shape: shapes.wave,
});

const sharedComponents = {
  MuiCssBaseline: {
    styleOverrides: `
      @font-face {
        font-family: 'Optimism Sans';
        src: url('../fonts/OptimismSans.woff2') format('woff2'),
             url('../fonts/OptimismSans.woff') format('woff'),
             url('../fonts/OptimismSans.ttf') format('truetype');
        font-weight: 400;
        font-style: normal;
      }
      
      @font-face {
        font-family: 'Optimism Sans';
        src: url('../fonts/OptimismSans.woff2') format('woff2'),
             url('../fonts/OptimismSans.woff') format('woff'),
             url('../fonts/OptimismSans.ttf') format('truetype');
        font-weight: 700;
        font-style: normal;
      }
      
      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
      }
    `,
  },
  BackstageSidebarItem: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: 'rgba(255, 128, 212, 0.12)', /* P2 light tint */
        },
      },
      label: {
        fontWeight: 600,
      },
    },
  },
  BackstageHeader: {
    styleOverrides: {
      header: {
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 200%',
        backgroundPosition: '50% 0px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      },
      title: {
        color: '#FFFFFF',
        fontWeight: 700,
      },
    },
  },
};

const lightComponents = {
  ...sharedComponents,
  MuiCssBaseline: {
    styleOverrides: `
      ${sharedComponents.MuiCssBaseline.styleOverrides}

      body {
        background-color: #F9F5F4;
      }

      #root {
        width: 100%;
        min-height: 100vh;
        background-color: #F9F5F4;
      }
    `,
  },
  BackstageHeader: {
    styleOverrides: {
      ...sharedComponents.BackstageHeader.styleOverrides,
      header: {
        ...sharedComponents.BackstageHeader.styleOverrides.header,
        backgroundImage: `url(${BaseSky1})`,
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      colorDefault: {
        color: '#5F016F',
      },
    },
  },
  BackstageSidebarSubmenuItem: {
    styleOverrides: {
      item: {
        // Double specificity (&&) to beat the component's own navigation.color rule
        '&&': { color: '#5F016F' },
        '&&:hover': { color: '#FFADE4' },
      },
      label: {
        color: '#5F016F',
      },
    },
  },
  BackstageSidebarSubmenu: {
    styleOverrides: {
      title: {
        color: '#5F016F',
      },
    },
  },
  BackstagePage: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: '#FFFFFF',
        boxShadow: 'none',
        borderRadius: 8,
      },
    },
  },
};

const darkComponents = {
  ...sharedComponents,
  MuiCssBaseline: {
    styleOverrides: `
      ${sharedComponents.MuiCssBaseline.styleOverrides}

      body {
        background-color: #433f3f;
      }

      #root {
        width: 100%;
        min-height: 100vh;
        background-color: #433f3f;
      }

    `,
  },
  BackstageHeader: {
    styleOverrides: {
      ...sharedComponents.BackstageHeader.styleOverrides,
      header: {
        ...sharedComponents.BackstageHeader.styleOverrides.header,
        backgroundImage: `url(${BaseSky6})`,
      },
    },
  },
  BackstagePage: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none', /* suppress MUI elevation gradient overlay */
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: '#424141', /* matches BUI --bui-bg-neutral-1 dark default */
        boxShadow: 'none',
        borderRadius: 8,
      },
    },
  },
};

export const arriveLightUnifiedTheme = createUnifiedTheme({
  fontFamily: '"Optimism Sans", Helvetica, Arial, sans-serif',
  defaultPageTheme: 'home',
  palette: {
    ...palettes.light,
    primary: {
      main: '#5F016F', /* P1 */
    },
    secondary: {
      main: '#FF33BB', /* P2 */
    },
    background: {
      default: '#F9F5F4', /* Off-White */
      paper: '#FFFFFF',
    },
    status: {
      ...(palettes.light.status || {}),
      ok: '#1DB954',
      warning: '#E36D05',
      error: '#E22134',
    },
    navigation: {
      background: '#5F016F',
      indicator: '#FF33BB',
      color: '#FFFFFF',
      selectedColor: '#FFADE4',
      navItem: {
        hoverBackground: 'rgba(255, 51, 187, 0.12)',
      },
      submenu: {
        background: '#FFFFFF',
      },
    },
    highlight: '#FF80D4',
    link: '#5F016F',
    linkHover: '#4a0059',
  },
  pageTheme: {
    home: arrivePageTheme,
    documentation: arrivePageTheme,
    tool: arrivePageTheme,
    service: arrivePageTheme,
    website: arrivePageTheme,
    library: arrivePageTheme,
    other: arrivePageTheme,
    app: arrivePageTheme,
  },
  components: lightComponents,
});

export const arriveDarkUnifiedTheme = createUnifiedTheme({
  fontFamily: '"Optimism Sans", Helvetica, Arial, sans-serif',
  defaultPageTheme: 'home',
  palette: {
    ...palettes.dark,
    primary: {
      main: '#FFC7EC', /* FFADE4 Brand light FFC7EC P1 variant for dark mode */
    },
    secondary: {
      main: '#5F016F', /* Does nothing */
    },
    background: {
      default: '#433f3f', /* Asset page table - original 111111 */
      paper: '#424141', /* matches BUI --bui-bg-neutral-1 dark default */
    },
    status: {
      ...(palettes.dark.status || {}),
      ok: '#1DB954',
      warning: '#E36D05',
      error: '#E22134',
    },
    navigation: {
      background: '#38003d', /* side bar background P1 dark variant */
      indicator: '#FF33BB', /* not used? */
      color: '#f5f5f5', /* Left menu text color */
      selectedColor: '#FFADE4', /* Left menu selected text color ff80d4 */
      navItem: {
        hoverBackground: 'rgba(255, 51, 187, 0.12)',
      },
      submenu: {
        background: '#1c1c1c', /*  Not used */
      },
    },
    highlight: '#FF33BB', /* Not used */
    link: '#FF80D4', /*  Not used */
  },
  pageTheme: {
    home: arrivePageTheme,
    documentation: arrivePageTheme,
    tool: arrivePageTheme,
    service: arrivePageTheme,
    website: arrivePageTheme,
    library: arrivePageTheme,
    other: arrivePageTheme,
    app: arrivePageTheme,
  },
  components: darkComponents,
});
